import { useMutation, useQuery, useQueryClient } from 'react-query'

import { teamsRepository, updateWindowUserData } from '@/src/api'
import type { UserData } from '@/src/api/TeamsRepository'

let hasWarnedUnexpectedCurrentUserPayload = false

const isUserData = (value: unknown): value is UserData => {
  if (!value || typeof value !== 'object') return false
  return 'user' in (value as Record<string, unknown>)
}

export const useCurrentUserQuery = () => {
  return useQuery<UserData | null>(['currentUser'], async () => {
    const response = await teamsRepository.getCurrentUser()
    const payload = response.data as unknown

    if (isUserData(payload)) {
      // TODO: remove usage of window object to obtain user data, instead rely on cached query data
      updateWindowUserData(payload)
      return payload
    }

    if (!hasWarnedUnexpectedCurrentUserPayload) {
      console.warn('useCurrentUserQuery: received unexpected current user payload, skipping update.')
      hasWarnedUnexpectedCurrentUserPayload = true
    }
    return null
  })
}

export const useTeamQuery = (teamId?: string) => {
  return useQuery(['team', teamId], () => teamsRepository.getTeam())
}

export const useAddMemberMutation = (onSuccess?: () => void, onError?: () => void) => {
  return useMutation((email: string) => teamsRepository.addMember(email), {
    onSuccess,
    onError,
  })
}

export const useUpdateTeamMutation = (onSuccess?: (data: any) => void, onError?: () => void) => {
  const queryClient = useQueryClient()
  return useMutation((name: string) => teamsRepository.updateTeam(name), {
    onSuccess: async (response) => {
      // Let the optimistic update complete first
      onSuccess?.(response.data)

      // Then only invalidate currentUser to update the sidebar/header
      // Don't invalidate team query since it's handled by optimistic update
      await queryClient.invalidateQueries(['currentUser'])
    },
    onError,
  })
}

export const useRemoveMemberMutation = (onSuccess?: () => void, onError?: () => void) => {
  return useMutation((memberId: number) => teamsRepository.removeMember(memberId), {
    onSuccess,
    onError,
  })
}

export const useUpdateAdminMutation = (onSuccess?: () => void, onError?: () => void) => {
  return useMutation(({ memberId, isAdmin }: { memberId: number; isAdmin: boolean }) => teamsRepository.updateAdmin(memberId, isAdmin), {
    onSuccess,
    onError,
  })
}

// Matches the meetings dashboard and its sub-pages (/hub/meetings,
// /hub/meetings/enterprise-setup) under the router basename.
const MEETINGS_PATH_PATTERN = /\/meetings(\/|$)/

export const useSwitchTeamMutation = (onSuccess?: (teamUuid: string, teamName: string) => void, onError?: () => void) => {
  const queryClient = useQueryClient()
  return useMutation((teamUuid: string) => teamsRepository.switchTeam(teamUuid), {
    onSuccess: async (response, teamUuid) => {
      // Clear the previous team's Agent Detection data before active queries
      // refetch, including integration details and publishable keys.
      await queryClient.resetQueries({
        predicate: (query) => {
          const key = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey
          return typeof key === 'string' && key.startsWith('porter-')
        },
      })
      // The meetings dashboard is team-scoped end to end — meetings, settings,
      // org users, and the seat/plan + team globals the page reads off `window`
      // (CURRENT_USER_TEAM_ID, BILLING_SYSTEM, feature flags) are all baked in at
      // page load and none of them are keyed on the team. Invalidating React
      // Query alone would leave the new team looking at the old team's data, so
      // reload outright when the switch happens from the meetings page.
      if (MEETINGS_PATH_PATTERN.test(window.location.pathname) || window.location.pathname.startsWith('/hub/identity-verifications')) {
        window.location.reload()
        return
      }
      // Invalidate both currentUser and team queries to refresh all team-related data
      await Promise.all([queryClient.invalidateQueries(['currentUser']), queryClient.invalidateQueries(['team'])])
      onSuccess?.(teamUuid, response.data.team_name)
    },
    onError,
  })
}

export const PINNED_SIDEBAR_ITEMS_QUERY_KEY = ['pinnedSidebarItems'] as const

export const usePinnedSidebarItemsQuery = (initialData?: string[]) => {
  return useQuery<string[]>(
    PINNED_SIDEBAR_ITEMS_QUERY_KEY,
    async () => {
      const response = await teamsRepository.getPinnedItems()
      return response.data?.pinned ?? []
    },
    {
      initialData,
      staleTime: 60_000,
    },
  )
}

export const usePinSidebarItemMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((path: string) => teamsRepository.pinItem(path), {
    onMutate: async (path: string) => {
      await queryClient.cancelQueries(PINNED_SIDEBAR_ITEMS_QUERY_KEY)
      const previous = queryClient.getQueryData<string[]>(PINNED_SIDEBAR_ITEMS_QUERY_KEY) || []
      if (!previous.includes(path)) {
        queryClient.setQueryData<string[]>(PINNED_SIDEBAR_ITEMS_QUERY_KEY, [...previous, path])
      }
      return { previous }
    },
    onError: (_err, _path, context) => {
      if (context?.previous) queryClient.setQueryData(PINNED_SIDEBAR_ITEMS_QUERY_KEY, context.previous)
    },
    // Settle the cache from the response itself. An invalidate-then-refetch
    // pattern would race against the in-flight POST: refetchOnWindowFocus
    // (default true) can fire a GET on click that reads pre-pin state and
    // overwrites the optimistic update before the POST completes.
    onSuccess: (response) => {
      queryClient.setQueryData<string[]>(PINNED_SIDEBAR_ITEMS_QUERY_KEY, response.data?.pinned ?? [])
    },
  })
}

export const useUnpinSidebarItemMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((path: string) => teamsRepository.unpinItem(path), {
    onMutate: async (path: string) => {
      await queryClient.cancelQueries(PINNED_SIDEBAR_ITEMS_QUERY_KEY)
      const previous = queryClient.getQueryData<string[]>(PINNED_SIDEBAR_ITEMS_QUERY_KEY) || []
      queryClient.setQueryData<string[]>(
        PINNED_SIDEBAR_ITEMS_QUERY_KEY,
        previous.filter((p) => p !== path),
      )
      return { previous }
    },
    onError: (_err, _path, context) => {
      if (context?.previous) queryClient.setQueryData(PINNED_SIDEBAR_ITEMS_QUERY_KEY, context.previous)
    },
    // See pin mutation: settle from the response, not via refetch.
    onSuccess: (response) => {
      queryClient.setQueryData<string[]>(PINNED_SIDEBAR_ITEMS_QUERY_KEY, response.data?.pinned ?? [])
    },
  })
}
